import type { IndustrialCaseSummary } from '@/domain/industrialCase/industrialCaseSummary';
import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import { useMediaQuery } from '@/hooks/shared/useMediaQuery';

import Area from '@/components/apps/layouts/shared/Area';
import { HomeIndustrialCaseCard } from '@/components/apps/pages/home/section/clientUseCases/HomeIndustrialCaseCard';
import { trackIndustrialCaseSelection } from '@/components/apps/pages/solution/industrial/industrialAnalytics';
import { buildIndustrialCaseListPath } from '@/components/apps/pages/solution/industrial/industrialCaseRoutes';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import { AniSlideUp } from '@/components/shared/animations/basic';

import theme from '@/styles/theme';

import { useSafetyCases } from '../useSafetyPageData';
import SafetyCaseCard from './SafetyCaseCard';

const CASES_SECTION_ID = 'case_studies';

type Props = {
  /** Published Safety cases from the customer-case board, for this language */
  cases: ReadonlyArray<IndustrialCaseSummary>;
};

/**
 * ### CasesSection
 *
 * Customer cases from the same board the public case list is built from,
 * filtered to Safety, as the dark case cards the home page uses, each
 * linking to its detail page; the title links to the Safety view of the
 * list. While no Safety case has been published, or while public case
 * discovery is off, the section falls back to the three deployments from
 * the 2026-09 brief so the page never shows an empty band.
 *
 * @param props Published cases prepared by the route
 * @returns The rendered customer cases section element
 * @see {@link useSafetyCases} The brief's deployments used as the fallback
 */
export default function CasesSection({ cases }: Props) {
  const { t } = useTranslation('common');
  const fallbackCases = useSafetyCases(t);
  const hasPublishedCases = cases.length > 0;

  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme`screens.lg`})`,
  });

  return (
    <section data-analytics-section={CASES_SECTION_ID}>
      <Area>
        <SectionTitle
          color="lighter"
          title={t('고객 사례')}
          description={t(
            '실제 환경에 적용되어 인정받고 있는 Listen AI 안전 솔루션 사례를 살펴보세요.',
          )}
          linkInfo={
            hasPublishedCases
              ? {
                  context: t('고객 사례'),
                  title: t('전체 보기'),
                  url: buildIndustrialCaseListPath('safety'),
                }
              : undefined
          }
        />

        <ul className="mt-[40px] grid gap-[16px] lg:mt-[clamp(40px,6.667vw,80px)] lg:grid-cols-3 lg:gap-[clamp(16px,2vw,24px)]">
          {hasPublishedCases
            ? cases.map((caseSummary, index) => (
                <li
                  key={caseSummary.slug}
                  className="min-w-0 [&>div]:h-full [&_.target]:h-full"
                >
                  <AniSlideUp delay={isDesktop ? (index % 3) * 0.15 : 0}>
                    <HomeIndustrialCaseCard
                      caseSummary={caseSummary}
                      metricLabel={t('핵심 지표')}
                      readLabel={t('사례 읽기')}
                      onSelect={() =>
                        trackIndustrialCaseSelection({
                          contentType: 'case_card',
                          itemId: caseSummary.slug,
                          caseIdentity: {
                            slug: caseSummary.slug,
                            solution: caseSummary.solution,
                            industry: caseSummary.industry,
                          },
                        })
                      }
                    />
                  </AniSlideUp>
                </li>
              ))
            : fallbackCases.map((item, index) => (
                // AniSlideUp 내부 요소(> div, .target)까지 높이를 채워야 카드 높이가 맞는다.
                <li key={item.id} className="[&>div]:h-full [&_.target]:h-full">
                  <AniSlideUp delay={isDesktop ? index * 0.15 : 0}>
                    <SafetyCaseCard
                      customer={item.customer}
                      logo={item.logo}
                      title={item.title}
                      points={item.points}
                      photos={item.photos}
                    />
                  </AniSlideUp>
                </li>
              ))}
        </ul>
      </Area>
    </section>
  );
}
